import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { dateToS, getCurrentDateTime } from '../../../util/dateUtil';
import { logException } from '../../../services/appInsights';
import { makeStyles } from '@material-ui/core/styles';
import {
  Box,
  Checkbox,
  FormControlLabel,
  Grid,
  Hidden,
  IconButton,
  Radio,
  RadioGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core';
import TableStyle from '../../../styles/TableStyle';
import Button from '../../CustomButtons/Button.js';
import TitleBar from '../../Shared/TitleBar';
import { inject, observer } from 'mobx-react';
import { btMediumGrayColor, btNavyBlueColor } from '../../../assets/jss/nextjs-material-kit-pro';
import PleaseWait from '../../Shared/PleaseWait';
import StaffListCards from './StaffListCards';
import { callApi } from '../../../util/apiWrapper';
import Paper from '@material-ui/core/Paper';
import EditIcon from '@material-ui/icons/Edit';
import EmailIcon from '@material-ui/icons/Email';
import AddEditStaffDialog from './StaffDialogs/AddEditStaffDialog';
import { isEmailValid, isStudent, isAlumni, isAdmin } from '../../../util/miscUtil';
import useErrorAlert from '../../../hooks/useErrorAlert';
import useToastMessage from '../../../hooks/useToastMessage';
import PageUnauthorized from '../../Shared/PageUnauthorized';

const tableStyles = makeStyles(TableStyle);

const useStyles = makeStyles(() => ({
  frame: {
    width: '100%',
  },
  container: {
    width: '100vw',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  alignPage: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
  titlebar: {
    backgroundColor: '#fff',
    padding: '30px 10px 5px 10px',
    margin: '-15px 0 15px 0',
    borderBottom: '1px inset #f0f0f0',
  },
  noLeftPadding: {
    paddingLeft: '0 !important',
  },
  sortHeader: {
    cursor: 'pointer',
    color: btNavyBlueColor,
  },
  nosortHeader: {
    color: btMediumGrayColor,
  },
  idLink: {
    color: btNavyBlueColor,
    fontSize: '0.85rem',
    padding: 0,
  },
  pointer: {
    cursor: 'pointer',
  },
  filter: {
    margin: '5px 0 15px 20px',
    borderRadius: '6px 0 0 6px',
    width: '65%',
  },
  toolMargins: {
    margin: '-8px 0 0 20px',
  },
  noRecordsMsg: {
    fontSize: '1.15rem',
    margin: 5,
  },
  centerRadioSorting: {
    marginLeft: 20,
    marginRight: 20,
    justifyContent: 'space-between',
  },
  radioLabel: {
    fontSize: '0.85rem',
    fontWeight: 500,
    '& svg': {
      width: '1.25rem',
      height: '1.25rem',
    },
  },
  radioRow: {
    width: '100%',
    justifyContent: 'space-between',
  },
  selectDropdown: {
    color: '#333',
    backgroundColor: '#fff',
  },
  menuItem: {
    '&:hover': {
      color: '#fff',
      backgroundColor: '#8c857b',
    },
  },
  circleIcon: {
    borderRadius: '50%',
    transition: 'transform 0.2s ease-in-out, background-color 0.2s ease-in-out',
  },
  circleIconClicked: {
    animation: '$pulse 0.6s ease-in-out',
  },
  '@keyframes pulse': {
    '0%': {
      transform: 'scale(1)',
    },
    '50%': {
      transform: 'scale(1.2)',
      backgroundColor: 'rgba(63, 81, 181, 0.2)',
    },
    '100%': {
      transform: 'scale(1)',
    },
  },
  rowButtonContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  standardButton: {
    cursor: 'pointer',
    width: '155px',
    margin: '0 10px 0 0',
  },
}));

const StaffTable = inject(
  'AdminStore',
  'AuthStore'
)(
  observer((props) => {
    const store = props.AdminStore;
    const auth = props.AuthStore;

    const history = useHistory();

    const classes = useStyles();
    const tableClasses = tableStyles();

    const showErrorAlert = useErrorAlert();
    const showEmailConfirmation = useToastMessage();

    const emptyStaff = {
      pidm: '',
      internal_id: '',
      position_title: '',
      position_class: '',
      office_phone: '',
      cell_phone: '',
      exclude_from_ndb_sync: false,
      user: {
        first_name: '',
        last_name: '',
        email: '',
        roles: '',
      },
    };

    const [showAllStaff, setShowAllStaff] = React.useState(false);
    const [sortBy, setSortBy] = React.useState('');
    const [sortDirection, setSortDirection] = React.useState('asc');
    const [filter, setFilter] = React.useState('');
    const [alladmins, setAllAdmins] = React.useState([]);
    const [adminList, setAdminList] = React.useState([]);
    const [selectedStaff, setSelectedStaff] = React.useState(emptyStaff);
    const [pageTitle, setPageTitle] = useState('Manage Staff Users');
    const [showAddEditDialog, setShowAddEditDialog] = React.useState(false);
    const [type, setType] = React.useState('ADD');
    const [allowWorkbookAccess, setAllowWorkbookAccess] = React.useState(false);
    const [hasWorkbookAccess, setHasWorkbookAccess] = React.useState(0);
    const [staffSyncPositionClassRoleMap, setStaffSyncPositionClassRoleMap] = React.useState([]);
    const [clickedEmailButton, setClickedEmailButton] = React.useState(null);

    useEffect(() => {
      if (auth.roles && auth.roles.length > 0) {
        if (isStudent(auth.roles) || isAlumni(auth.roles)) {
          history.push('/seniorplanner');
        }
      }
    }, [auth.roles, history]);

    useEffect(() => {
      if (auth) {
        fetchAdmins();
        fetchStaffSyncPositionClassRoleMap();
      }
    }, [auth, store]);

    //Show All Staff checkbox
    useEffect(() => {
      if (auth) {
        if (showAllStaff) {
          setAdminList(alladmins);
        } else {
          setAdminList(alladmins.filter((a) => a.user.is_active));
        }
      }
    }, [auth, alladmins, showAllStaff]);

    // Sort and filter
    useEffect(() => {
      const compareValues = (key, order = 'asc') => {
        return function innerSort(a, b) {
          if (key === 'email') {
            let aVal = '';
            let bVal = '';
            if (a.user[key]) {
              aVal = a.user[key] ? a.user[key] : '';
            }
            if (b.user[key]) {
              bVal = b.user[key] ? b.user[key] : '';
            }
            const varA = standarizeEmail(aVal);
            const varB = standarizeEmail(bVal);
            let comparison = 0;
            if (varA > varB) {
              comparison = 1;
            } else if (varA < varB) {
              comparison = -1;
            }
            return order === 'desc' ? comparison * -1 : comparison;
          } else if (key === 'first_name' || key === 'last_name') {
            let aVal = '';
            let bVal = '';
            if (a.user[key]) {
              aVal = a.user[key] ? a.user[key] : '';
            }
            if (b.user[key]) {
              bVal = b.user[key] ? b.user[key] : '';
            }
            const varA = aVal;
            const varB = bVal;
            let comparison = 0;
            if (varA > varB) {
              comparison = 1;
            } else if (varA < varB) {
              comparison = -1;
            }
            return order === 'desc' ? comparison * -1 : comparison;
          } else if (key === 'roles') {
            let aVal = '';
            let bVal = '';
            if (a.user[key]) {
              aVal = a.user[key] ? a.user[key].toString() : '';
            }
            if (b.user[key]) {
              bVal = b.user[key] ? b.user[key].toString() : '';
            }
            const varA = aVal;
            const varB = bVal;
            let comparison = 0;
            if (varA > varB) {
              comparison = 1;
            } else if (varA < varB) {
              comparison = -1;
            }
            return order === 'desc' ? comparison * -1 : comparison;
          } else if (key === 'updated_at' || key === 'last_login') {
            let aVal = '';
            let bVal = '';
            if (a.user[key]) {
              aVal = a.user[key] ? a.user[key] : '';
            }
            if (b.user[key]) {
              bVal = b.user[key] ? b.user[key] : '';
            }
            const varA = aVal;
            const varB = bVal;
            let comparison = 0;
            if (varA > varB) {
              comparison = 1;
            } else if (varA < varB) {
              comparison = -1;
            }
            return order === 'desc' ? comparison * -1 : comparison;
          } else if (key === 'is_active') {
            let aVal = '';
            let bVal = '';
            if (a[key]) {
              aVal = a[key];
            }
            if (b[key]) {
              bVal = b[key];
            }
            const varA = aVal ? (a.user['last_login'] ? 0 : 1) : 2;
            const varB = bVal ? (b.user['last_login'] ? 0 : 1) : 2;
            let comparison = 0;
            if (varA > varB) {
              comparison = 1;
            } else if (varA < varB) {
              comparison = -1;
            }
            return order === 'desc' ? comparison * -1 : comparison;
          } else {
            let aVal = '';
            let bVal = '';
            if (a[key]) {
              aVal = a[key] ? a[key] : '';
            }
            if (b[key]) {
              bVal = b[key] ? b[key] : '';
            }
            const varA = aVal;
            const varB = bVal;
            let comparison = 0;
            if (varA > varB) {
              comparison = 1;
            } else if (varA < varB) {
              comparison = -1;
            }
            return order === 'desc' ? comparison * -1 : comparison;
          }
        };
      };

      let filteredData = adminList;

      // Filter Field
      if (filter && filter.length > 0) {
        const filterNoSpaces = (filter || '').replace(/\s+/g, '');
        const filterLower = filterNoSpaces.toLowerCase();

        filteredData = filteredData.filter(
          (s) =>
            (s.user.email && standarizeEmail(s.user.email).toString().toLowerCase().includes(filterLower)) ||
            (s.user.first_name && s.user.first_name.toLowerCase().includes(filterLower)) ||
            (s.user.last_name && s.user.last_name.toLowerCase().includes(filterLower)) ||
            (s.position_title && s.position_title.toLowerCase().includes(filterLower))
        );
      } else {
        // Show all based on showAllStaff value
        if (showAllStaff) {
          handleSetAdminList(alladmins);
          filteredData = alladmins;
        } else {
          const activeStaff = alladmins.filter((a) => a.user.is_active);
          handleSetAdminList(activeStaff);
          filteredData = activeStaff;
        }
      }

      store.setPage(0);
      if (store.showAllRecords) {
        store.setTotalAdmins(filteredData.length);
      } else {
        filteredData = filteredData.filter((x) => x.departure_date === undefined);
        store.setTotalAdmins(filteredData.length);
      }

      // SORTING
      filteredData = filteredData.sort(compareValues(sortBy, sortDirection));

      setAdminList(filteredData);
      store.setAdminData(filteredData);
    }, [store, sortBy, filter, sortDirection, alladmins, showAllStaff]);

    async function fetchStaffSyncPositionClassRoleMap() {
      try {
        store.setLoading(true);
        store.setSearching(true);

        const data = await callApi(auth, 'staff/positionclass/role/map', 'GET');
        const result = data && data.result;

        setStaffSyncPositionClassRoleMap(result);

        store.setSearching(false);
        store.setLoading(false);
        setSortBy('last_name');
      } catch (error) {
        logException(error);
        store.setSearching(false);
        store.setLoading(false);
        console.warn('Error fetching staff list:', error);
      }
    }

    async function fetchAdmins() {
      try {
        store.setLoading(true);
        store.setSearching(true);

        setShowAllStaff(false);
        const data = await callApi(auth, 'admin', 'GET');
        const result = data && data.result;

        handleSetSelectedStaff(null);

        handleSetAllAdmins(result);

        const activeStaff = result.filter((a) => a.user.is_active);

        handleSetAdminList(activeStaff);

        store.setTotalAdmins(activeStaff.length);

        store.setSearching(false);
        store.setLoading(false);
        setSortBy('last_name');
      } catch (error) {
        logException(error);
        store.setSearching(false);
        store.setLoading(false);
        console.warn('Error fetching staff list:', error);
      }
    }

    const setSort = (event, val) => {
      event.preventDefault();
      event.stopPropagation();
      if (val) {
        if (val === sortBy) {
          if (sortDirection === 'asc') {
            setSortDirection('desc');
          } else {
            setSortDirection('asc');
          }
        } else {
          setSortBy(val);
          setSortDirection('asc');
        }
      }
    };

    const filterData = (val) => {
      setFilter(val);
    };

    const handleNewStaff = () => {
      setType('ADD');
      setSelectedStaff(emptyStaff);
      setAllowWorkbookAccess(false);
      setHasWorkbookAccess(0);
      setShowAddEditDialog(true);
    };

    const handleEditStaff = (staff) => {
      setType('EDIT');
      const editStaff = {
        id: staff.id,
        is_active: staff.is_active,
        pidm: staff.pidm,
        internal_id: staff.internal_id,
        position_title: staff.position_title,
        position_class: staff.position_class,
        office_phone: staff.office_phone,
        cell_phone: staff.cell_phone,
        exclude_from_ndb_sync: staff.exclude_from_ndb_sync,
        userId: staff.userId,
        user: {
          id: staff.user.id,
          first_name: staff.user.first_name,
          last_name: staff.user.last_name,
          email: staff.user.email,
          roles: staff.user.roles.toString(),
          created_at: staff.user.created_at,
          updated_at: staff.user.updated_at,
          last_login: staff.user.last_login,
          is_active: staff.user.is_active,
        },
      };

      // Reset state before fetching to prevent stale data
      setHasWorkbookAccess(0);
      setAllowWorkbookAccess(false);

      // Check if this staff user has a senior plan assignment
      fetchAllowWorkbookAccess(staff.user.id).then((result) => {
        setAllowWorkbookAccess(result);
      });
      setSelectedStaff(editStaff);
      setShowAddEditDialog(true);
    };

    const showInactiveData = (active) => {
      return active ? 'inherit' : 'gray';
    };

    const handleSetShowAllStaff = (val) => {
      setShowAllStaff(val);
      store.setShowAllStaff(val);
      if (val) {
        handleSetAdminList(alladmins);
      } else {
        const activeStaff = alladmins.filter((a) => a.user.is_active);
        handleSetAdminList(activeStaff);
      }
    };

    const handleSetSelectedStaff = (staff) => {
      setSelectedStaff(staff);
      store.setSelectedStaff(staff);
    };

    const handleSetAdminList = (list) => {
      setAdminList(list);
      store.setAdminData(list);
      store.setTotalAdmins(list.length);
    };

    const handleSetAllAdmins = (list) => {
      setAllAdmins(list);
      store.setAllAdmins(list);
      store.setTotalAdmins(list.length);
    };

    const handleAddEditDialogClose = (event, reason) => {
      if (reason === 'backdropClick') {
        setShowAddEditDialog(true);
      } else {
        setShowAddEditDialog(false);
        setAllowWorkbookAccess(false);
        setHasWorkbookAccess(0);
      }
    };

    const fetchAllowWorkbookAccess = async (userId) => {
      const plan = await callApi(auth, 'seniorplan/assignment/' + userId, 'GET');
      store.setLoading(false);
      if (plan && plan.result && plan.result.length > 0) {
        setHasWorkbookAccess(plan.result[0].id);
        return true;
      }
      return false;
    };

    const canSaveStaff = () => {
      if (!selectedStaff?.user?.first_name) return false;
      if (!selectedStaff?.user?.last_name) return false;
      const email = selectedStaff?.user?.email || '';
      if (!email || email.length < 3 || !isEmailValid(email)) return false;
      if (!selectedStaff?.internal_id) return false;
      if (!selectedStaff?.position_title) return false;
      if (!selectedStaff?.position_class) return false;
      return true;
    };

    const handleSaveStaff = async () => {
      if (!canSaveStaff()) {
        showErrorAlert('Please fill in all required fields and ensure the email is valid.');
        return;
      }
      try {
        store.setLoading(true);
        store.setSearching(true);

        const seniorPlans = await callApi(auth, 'seniorplan', 'GET');
        const latestSeniorPlan =
          seniorPlans && seniorPlans.result && seniorPlans.result.length > 0
            ? seniorPlans.result.sort((a, b) => new Date(b.id) - new Date(a.id))[0]
            : null;

        if (type === 'ADD') {
          const item = {
            'pidm': selectedStaff?.pidm ? selectedStaff.pidm : '',
            'internal_id': selectedStaff?.internal_id ? selectedStaff.internal_id : '',
            'position_title': selectedStaff?.position_title ? selectedStaff.position_title : '',
            'user': {
              'email': selectedStaff?.user?.email ? selectedStaff.user.email : '',
              'first_name': selectedStaff?.user?.first_name ? selectedStaff.user.first_name : '',
              'last_name': selectedStaff?.user?.last_name ? selectedStaff.user.last_name : '',
              'is_active': true,
              'created_at': getCurrentDateTime(),
              'roles': [selectedStaff?.user.roles ? selectedStaff.user.roles : ''],
            },
            'is_active': true,
            'exclude_from_ndb_sync': selectedStaff?.exclude_from_ndb_sync ? selectedStaff.exclude_from_ndb_sync : false,
            'position_class': selectedStaff?.position_class ? selectedStaff.position_class : '',
            'office_phone': selectedStaff?.office_phone ? selectedStaff.office_phone : '',
            'cell_phone': selectedStaff?.cell_phone ? selectedStaff.cell_phone : '',
          };

          const app = await callApi(auth, 'staff', 'POST', {
            item: item,
          });

          if (app.result) {
            fetchAdmins();
          }
        } else {
          const item = {
            'id': selectedStaff?.id,
            'pidm': selectedStaff?.pidm ? selectedStaff.pidm : '',
            'internal_id': selectedStaff?.internal_id ? selectedStaff.internal_id : '',
            'position_title': selectedStaff?.position_title ? selectedStaff.position_title : '',
            'user': {
              'id': selectedStaff?.user?.id,
              'email': selectedStaff?.user?.email ? selectedStaff.user.email : '',
              'first_name': selectedStaff?.user?.first_name ? selectedStaff.user.first_name : '',
              'last_name': selectedStaff?.user?.last_name ? selectedStaff.user.last_name : '',
              'is_active': selectedStaff?.user?.is_active ? selectedStaff.user.is_active : false,
              'created_at': selectedStaff?.user?.created_at ? selectedStaff.user.created_at : getCurrentDateTime(),
              'last_login': selectedStaff?.user?.last_login ? selectedStaff.user.last_login : null,
              'roles': [selectedStaff?.user.roles ? selectedStaff.user.roles : ''],
            },
            'userId': selectedStaff?.user?.id ? selectedStaff?.user?.id : 0,
            'exclude_from_ndb_sync': selectedStaff?.exclude_from_ndb_sync ? selectedStaff.exclude_from_ndb_sync : false,
            'is_active': selectedStaff.is_active ? selectedStaff.is_active : false,
            'position_class': selectedStaff?.position_class ? selectedStaff.position_class : '',
            'office_phone': selectedStaff?.office_phone ? selectedStaff.office_phone : '',
            'cell_phone': selectedStaff?.cell_phone ? selectedStaff.cell_phone : '',
            'updated_at': getCurrentDateTime(),
          };

          const app = await callApi(auth, 'staff', 'PUT', {
            item: item,
          });

          if (app.result) {
            fetchAdmins();
          }

          // Clear filter field
          setFilter('');
          document.getElementById('filter-field').value = '';

          // if adding workbook access and they don't already have it, add it.
          if (allowWorkbookAccess && hasWorkbookAccess === 0) {
            await callApi(auth, 'seniorplan/staff', 'POST', {
              item: {
                'user_id': selectedStaff?.user?.id,
                'senior_plan_id': latestSeniorPlan.id,
              },
            });
          }

          // If removing workbook access and they have it, remove it.
          if (!allowWorkbookAccess && hasWorkbookAccess > 0) {
            await callApi(auth, `seniorplan/staff/${hasWorkbookAccess}`, 'DELETE', {});
          }

          // If setting user to inactive and they have workbook access, remove it.
          if (!selectedStaff.user.is_active && hasWorkbookAccess > 0) {
            await callApi(auth, `seniorplan/staff/${hasWorkbookAccess}`, 'DELETE', {});
          }
        }

        store.setSearching(false);
        store.setLoading(false);

        setShowAddEditDialog(false);
      } catch (error) {
        logException(error);
        store.setSearching(false);
        store.setLoading(false);
        console.error('Error writing Successful Futures staff:', error);
      }
    };

    // Saving space in table.
    const displayRole = (role) => {
      return role.replace('AFTERCARE_SPECIALIST', 'SF').replace('AFTERCARE_COORDINATOR', 'SF');
    };

    const standarizeEmail = (e) => {
      if (e) {
        const emailParts = e.split('@');
        const domainParts = emailParts[1].split('.');
        return emailParts[0].toLowerCase() + '@' + domainParts[0].toLowerCase() + '.' + domainParts[1].toLowerCase();
      } else {
        return '';
      }
    };

    const sendStaffLoginNotification = async (email) => {
      try {
        setClickedEmailButton(email);
        await callApi(auth, 'staff/email', 'POST', { Email: email });
        setTimeout(() => {
          setClickedEmailButton(null);
          showEmailConfirmation('Email successfully sent.', 'success');
        }, 600);
      } catch (error) {
        logException(error);
        console.log('Error sending staff login notification email:', error);
        setClickedEmailButton(null);
      }
    };

    const handleCardSortChange = (event) => {
      setSort(event, event.target.value);
    };

    const emptyRows = store.perPage - Math.min(store.perPage, adminList?.length - store.page * store.perPage);

    if (auth.roles && auth.roles.length > 0 && !isAdmin(auth.roles)) {
      return <PageUnauthorized />;
    }

    return (
      <div className={classes.frame}>
        {auth.roles && auth.roles.length > 0 && isAdmin(auth.roles) && (
          <div className={classes.container}>
            <Box
              sx={{
                width: '100%',
                maxWidth: {
                  xs: '380px',
                  sm: '540px',
                  md: '868px',
                  lg: '1320px',
                  xl: '1320px',
                },
              }}
            >
              <Grid container>
                {auth.roles && !isStudent(auth.roles) && !isAlumni(auth.roles) && (
                  <>
                    <PleaseWait isLoading={store.loading} />
                    <TitleBar title={pageTitle} currentPage='Manage Staff Users' paths={[{ path: '/', name: 'Home' }]} additionalInformation='' />
                    <div style={{ width: '100%' }}>
                      <Grid container>
                        <Grid item xs={12}>
                          <Grid container direction='row' style={{ height: '60px' }}>
                            <Grid
                              item
                              xs={12}
                              sm={12}
                              md={8}
                              lg={6}
                              align='left'
                              style={{
                                display: 'flex',
                                flexDirection: 'row',
                                justifyContent: 'flex-start',
                                alignItems: 'center',
                              }}
                            >
                              <TextField
                                id='filter-field'
                                type='search'
                                className={classes.filter}
                                size='small'
                                hiddenLabel
                                placeholder='Filter Name, Email, or Title'
                                variant='outlined'
                                onChange={(event) => filterData(event.target.value)}
                                clearable='true'
                              />
                              <Hidden smDown implementation='css' className={classes.toolMargins}>
                                <FormControlLabel
                                  control={<Checkbox color='default' />}
                                  label='Show All Staff'
                                  onChange={() => handleSetShowAllStaff(!store.showAllStaff)}
                                  checked={showAllStaff}
                                />
                              </Hidden>
                            </Grid>
                            <Grid item xs={12} sm={12} md={4} lg={6} align='right'>
                              <Hidden mdDown implementation='css'>
                                <Button
                                  className={classes.standardButton}
                                  color='primary'
                                  variant='contained'
                                  round
                                  onClick={(event) => handleNewStaff()}
                                >
                                  Add Staff
                                </Button>
                              </Hidden>
                            </Grid>
                          </Grid>
                          <Hidden mdDown implementation='css'>
                            <TableContainer component={Paper} style={{ margin: '0 auto', maxWidth: '100%' }}>
                              <Table className={tableClasses.table}>
                                <TableHead>
                                  <TableRow style={{ width: '100%' }}>
                                    <Tooltip title='Click to sort by email.' placement='top'>
                                      <TableCell
                                        onClick={(event) => setSort(event, 'email')}
                                        className={classes.sortHeader}
                                        style={{ width: '280px' }}
                                      >
                                        EMAIL
                                        <span
                                          className='fas fa-caret-up'
                                          style={{
                                            display: sortBy === 'email' ? (sortDirection === 'asc' ? 'inline-block' : 'none') : 'none',
                                          }}
                                        />
                                        <span
                                          className='fas fa-caret-down'
                                          style={{
                                            display: sortBy === 'email' ? (sortDirection === 'desc' ? 'inline-block' : 'none') : 'none',
                                          }}
                                        />
                                      </TableCell>
                                    </Tooltip>
                                    <Tooltip title='Click to sort by first name.' placement='top'>
                                      <TableCell
                                        onClick={(event) => setSort(event, 'first_name')}
                                        className={classes.sortHeader}
                                        style={{ width: '120px' }}
                                      >
                                        FIRST NAME{' '}
                                        <span
                                          className='fas fa-caret-up'
                                          style={{
                                            display: sortBy === 'first_name' ? (sortDirection === 'asc' ? 'inline-block' : 'none') : 'none',
                                          }}
                                        />
                                        <span
                                          className='fas fa-caret-down'
                                          style={{
                                            display: sortBy === 'first_name' ? (sortDirection === 'desc' ? 'inline-block' : 'none') : 'none',
                                          }}
                                        />
                                      </TableCell>
                                    </Tooltip>
                                    <Tooltip title='Click to sort by last name.' placement='top'>
                                      <TableCell
                                        onClick={(event) => setSort(event, 'last_name')}
                                        className={classes.sortHeader}
                                        style={{ width: '120px' }}
                                      >
                                        LAST NAME{' '}
                                        <span
                                          className='fas fa-caret-up'
                                          style={{
                                            display: sortBy === 'last_name' ? (sortDirection === 'asc' ? 'inline-block' : 'none') : 'none',
                                          }}
                                        />
                                        <span
                                          className='fas fa-caret-down'
                                          style={{
                                            display: sortBy === 'last_name' ? (sortDirection === 'desc' ? 'inline-block' : 'none') : 'none',
                                          }}
                                        />
                                      </TableCell>
                                    </Tooltip>
                                    <Tooltip title='Click to sort by role.' placement='top'>
                                      <TableCell
                                        onClick={(event) => setSort(event, 'roles')}
                                        className={classes.sortHeader}
                                        style={{ width: '140px' }}
                                      >
                                        ROLE{' '}
                                        <span
                                          className='fas fa-caret-up'
                                          style={{
                                            display: sortBy === 'roles' ? (sortDirection === 'asc' ? 'inline-block' : 'none') : 'none',
                                          }}
                                        />
                                        <span
                                          className='fas fa-caret-down'
                                          style={{
                                            display: sortBy === 'roles' ? (sortDirection === 'desc' ? 'inline-block' : 'none') : 'none',
                                          }}
                                        />
                                      </TableCell>
                                    </Tooltip>
                                    <Tooltip title='Click to sort by title.' placement='top'>
                                      <TableCell
                                        onClick={(event) => setSort(event, 'position_title')}
                                        className={classes.sortHeader}
                                        style={{ width: '210px' }}
                                      >
                                        TITLE{' '}
                                        <span
                                          className='fas fa-caret-up'
                                          style={{
                                            display: sortBy === 'position_title' ? (sortDirection === 'asc' ? 'inline-block' : 'none') : 'none',
                                          }}
                                        />
                                        <span
                                          className='fas fa-caret-down'
                                          style={{
                                            display: sortBy === 'position_title' ? (sortDirection === 'desc' ? 'inline-block' : 'none') : 'none',
                                          }}
                                        />
                                      </TableCell>
                                    </Tooltip>
                                    <Tooltip title='Click to sort by last login.' placement='top'>
                                      <TableCell
                                        onClick={(event) => setSort(event, 'last_login')}
                                        className={classes.sortHeader}
                                        style={{ width: '120px', textAlign: 'center' }}
                                      >
                                        LAST LOGIN{' '}
                                        <span
                                          className='fas fa-caret-up'
                                          style={{
                                            display: sortBy === 'last_login' ? (sortDirection === 'asc' ? 'inline-block' : 'none') : 'none',
                                          }}
                                        />
                                        <span
                                          className='fas fa-caret-down'
                                          style={{
                                            display: sortBy === 'last_login' ? (sortDirection === 'desc' ? 'inline-block' : 'none') : 'none',
                                          }}
                                        />
                                      </TableCell>
                                    </Tooltip>
                                    <Tooltip title='Click to sort by last updated.' placement='top'>
                                      <TableCell
                                        onClick={(event) => setSort(event, 'updated_at')}
                                        className={classes.sortHeader}
                                        style={{ width: '130px', textAlign: 'center' }}
                                      >
                                        LAST UPDATED{' '}
                                        <span
                                          className='fas fa-caret-up'
                                          style={{
                                            display: sortBy === 'updated_at' ? (sortDirection === 'asc' ? 'inline-block' : 'none') : 'none',
                                          }}
                                        />
                                        <span
                                          className='fas fa-caret-down'
                                          style={{
                                            display: sortBy === 'updated_at' ? (sortDirection === 'desc' ? 'inline-block' : 'none') : 'none',
                                          }}
                                        />
                                      </TableCell>
                                    </Tooltip>
                                    <TableCell
                                      onClick={(event) => setSort(event, 'is_active')}
                                      className={classes.sortHeader}
                                      style={{ width: '100px', textAlign: 'center' }}
                                    >
                                      STATUS{' '}
                                      <span
                                        className='fas fa-caret-up'
                                        style={{
                                          display: sortBy === 'is_active' ? (sortDirection === 'asc' ? 'inline-block' : 'none') : 'none',
                                        }}
                                      />
                                      <span
                                        className='fas fa-caret-down'
                                        style={{
                                          display: sortBy === 'is_active' ? (sortDirection === 'desc' ? 'inline-block' : 'none') : 'none',
                                        }}
                                      />
                                    </TableCell>
                                    <TableCell className={classes.noSortHeader} style={{ width: '90px', textAlign: 'center' }}>
                                      ACTIONS
                                    </TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {store.searching ? (
                                    <TableRow style={{ width: '100%' }}>
                                      <TableCell align='center' colSpan={8}>
                                        <Typography variant='h5'>Loading...</Typography>
                                      </TableCell>
                                    </TableRow>
                                  ) : (
                                    adminList?.slice(store.page * store.perPage, store.page * store.perPage + store.perPage).map((admin, idx) => (
                                      <TableRow style={{ width: '100%', cursor: 'pointer' }} key={admin.user.id + '_' + idx}>
                                        <TableCell style={{ width: '280px', color: showInactiveData(admin.user.is_active) }}>
                                          {admin.user.email}
                                        </TableCell>
                                        <TableCell style={{ width: '120px', color: showInactiveData(admin.user.is_active) }}>
                                          {admin.user.first_name}
                                        </TableCell>
                                        <TableCell style={{ width: '120px', color: showInactiveData(admin.user.is_active) }}>
                                          {admin.user.last_name}
                                        </TableCell>
                                        <TableCell style={{ width: '140px', color: showInactiveData(admin.user.is_active) }}>
                                          {displayRole(admin.user.roles.toString())}
                                        </TableCell>
                                        <TableCell style={{ width: '210px', color: showInactiveData(admin.user.is_active) }}>
                                          {admin.position_title}
                                        </TableCell>
                                        <TableCell style={{ width: '120px', color: showInactiveData(admin.user.is_active), textAlign: 'center' }}>
                                          {dateToS(admin.user.last_login)}
                                        </TableCell>
                                        <TableCell style={{ width: '130px', color: showInactiveData(admin.user.is_active), textAlign: 'center' }}>
                                          {dateToS(admin.user.updated_at)}
                                        </TableCell>
                                        <TableCell style={{ width: '100px', color: showInactiveData(admin.user.is_active), textAlign: 'center' }}>
                                          {admin.user.is_active ? (!admin.user.last_login ? 'Pending' : 'Active') : 'Inactive'}
                                        </TableCell>
                                        <TableCell style={{ width: '90px', textAlign: 'center' }}>
                                          <div className={classes.rowButtonContainer}>
                                            <IconButton
                                              className={classes.circleIcon}
                                              color='primary'
                                              variant='contained'
                                              size='small'
                                              aria-label='Edit'
                                              onClick={(e) => {
                                                handleEditStaff(admin);
                                              }}
                                            >
                                              <EditIcon style={{ fontSize: 'small' }} />
                                            </IconButton>
                                            {admin.user.is_active ? (
                                              <Tooltip title='Re-send invitation email' placement='top'>
                                                <IconButton
                                                  className={`${classes.circleIcon} ${
                                                    clickedEmailButton === admin.user.email ? classes.circleIconClicked : ''
                                                  }`}
                                                  size='small'
                                                  color='primary'
                                                  variant='contained'
                                                  aria-label='Email'
                                                  onClick={(e) => {
                                                    sendStaffLoginNotification(admin.user.email);
                                                  }}
                                                >
                                                  <EmailIcon style={{ fontSize: 'small' }} />
                                                </IconButton>
                                              </Tooltip>
                                            ) : (
                                              ''
                                            )}
                                          </div>
                                        </TableCell>
                                      </TableRow>
                                    ))
                                  )}
                                  {!store.searching && adminList && adminList.length === 0 ? (
                                    <TableRow>
                                      <TableCell align='center' colSpan={8} className={classes.noLeftPadding}>
                                        <summary className={classes.noRecordsMsg}>No records found.</summary>
                                      </TableCell>
                                    </TableRow>
                                  ) : (
                                    <React.Fragment />
                                  )}
                                  {emptyRows > 0 && (
                                    <TableRow style={{ height: 34 * emptyRows }}>
                                      <TableCell colSpan={8} />
                                    </TableRow>
                                  )}
                                </TableBody>
                              </Table>
                              <TablePagination
                                component='div'
                                colSpan={8}
                                SelectProps={{
                                  InputProps: { 'aria-label': 'rows per page' },
                                  MenuProps: { classes: { paper: classes.selectDropdown } },
                                }}
                                classes={{ menuItem: classes.menuItem }}
                                rowsPerPageOptions={[10, 15, 25, 50, 100, 150, 200, 250]}
                                count={store.adminPages}
                                rowsPerPage={store.perPage}
                                page={store.page}
                                onPageChange={(ev, val) => store.handlePageChange(ev, val)}
                                onRowsPerPageChange={(ev) => store.handleChangeRowsPerPage(ev)}
                              />
                            </TableContainer>
                          </Hidden>
                          <Hidden lgUp implementation='css'>
                            <Grid container>
                              <Grid item xs={12} sm={12} md={5} className={classes.radioRow}>
                                <RadioGroup
                                  aria-label='card-sorting'
                                  name='date-group'
                                  className={classes.centerRadioSorting}
                                  row
                                  onChange={handleCardSortChange}
                                >
                                  <FormControlLabel
                                    checked={sortBy === 'email' ? true : false}
                                    control={<Radio color='default' />}
                                    label={<span className={classes.radioLabel}>Sort by Email</span>}
                                    onClick={(event) => setSort(event, 'email')}
                                    className={classes.radioLabel}
                                  />
                                  <span
                                    style={{
                                      marginLeft: '-15px',
                                      marginTop: '8px',
                                      marginRight: '10px',
                                    }}
                                  >
                                    <span
                                      className='fas fa-caret-up'
                                      style={{
                                        display: sortBy === 'email' ? (sortDirection === 'asc' ? 'inline-block' : 'none') : 'none',
                                      }}
                                    />
                                    <span
                                      className='fas fa-caret-down'
                                      style={{
                                        display: sortBy === 'email' ? (sortDirection === 'desc' ? 'inline-block' : 'none') : 'none',
                                      }}
                                    />
                                  </span>
                                  <FormControlLabel
                                    checked={sortBy === 'last_name' ? true : false}
                                    control={<Radio color='default' />}
                                    label={<span className={classes.radioLabel}>Sort by Last Name</span>}
                                    onClick={(event) => setSort(event, 'last_name')}
                                    className={classes.radioLabel}
                                    style={{
                                      marginLeft: '20px',
                                    }}
                                  />
                                  <span
                                    style={{
                                      marginLeft: '-15px',
                                      marginTop: '8px',
                                      marginRight: '10px',
                                    }}
                                  >
                                    <span
                                      className='fas fa-caret-up'
                                      style={{
                                        display: sortBy === 'last_name' ? (sortDirection === 'asc' ? 'inline-block' : 'none') : 'none',
                                      }}
                                    />
                                    <span
                                      className='fas fa-caret-down'
                                      style={{
                                        display: sortBy === 'last_name' ? (sortDirection === 'desc' ? 'inline-block' : 'none') : 'none',
                                      }}
                                    />
                                  </span>
                                </RadioGroup>
                              </Grid>
                              <Grid item xs={12} sm={12} md={4} lg={6} align='center'>
                                <Button
                                  className={classes.standardButton}
                                  color='primary'
                                  variant='contained'
                                  round
                                  onClick={(event) => handleNewStaff()}
                                >
                                  Add Staff
                                </Button>
                              </Grid>
                            </Grid>
                            {adminList && adminList.length > 0 && <StaffListCards adminList={adminList} handleEditStaff={handleEditStaff} />}
                          </Hidden>
                        </Grid>
                      </Grid>
                    </div>
                  </>
                )}
              </Grid>
            </Box>
          </div>
        )}
        <AddEditStaffDialog
          dialogTitle={type === 'ADD' ? 'Add Staff User' : 'Edit Staff User'}
          instructions={
            type === 'ADD'
              ? 'Please fill in the new staff user details and click save to add.'
              : 'Please modify staff user details and click save to update.'
          }
          showDialog={showAddEditDialog}
          handleClose={handleAddEditDialogClose}
          saveStaff={handleSaveStaff}
          selectedStaff={selectedStaff}
          setSelectedStaff={setSelectedStaff}
          type={type}
          allowWorkbookAccess={allowWorkbookAccess}
          setAllowWorkbookAccess={setAllowWorkbookAccess}
          staffSyncPositionClassRoleMap={staffSyncPositionClassRoleMap}
          isEmailValid={isEmailValid}
        />
      </div>
    );
  })
);

export default StaffTable;
